import React from 'react';

import { ApolloProvider } from '@apollo/client';
import Honeybadger from '@honeybadger-io/js';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import axios from 'axios';
import { beforeNotifyHandler } from 'honeybadger.utils';
import App, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { UserAuthContext } from 'server/auth';
import { TenantData } from 'server/config-cache';
import AnalyticsWrapper from 'src/analytics/AnalyticsWrapper';
import { GTMAnalytics } from 'src/analytics/GTMAnlaytics';
import ConfigContext from 'src/context/config';
import UserContext from 'src/context/user';
import { useApollo } from 'src/utils/apolloClient';
import { isServerSide } from 'src/utils/next';

import { getNextConfigValues } from '../../next-config';
import '../styles/global.css';
import { UserQuery } from '../ts/@types/generated/graphql/rails/graphql';
import ErrorPage from './_error';

/**
 * Register event handler on HoneyBadger -- if we are client side and the
 * log fails, attempt to log again by proxying through our server.
 *
 * This is only called client side because its an attempt to handle situations where the request
 * from the end user client directly to HoneyBadger is rejected, but it still might be accepted
 * when proxied through our server; when server side, the rejected call already came from our server,
 * so calling again will likely not help.
 *
 * We might lose some granularity of log as we only get the "notice" here but better than nothing.
 */
const { publicRuntimeConfig } = getNextConfigValues();
const { HONEYBADGER_API_KEY, HONEYBADGER_ENV, HONEYBADGER_REVISION } =
  publicRuntimeConfig;

Honeybadger.configure({
  apiKey: HONEYBADGER_API_KEY,
  revision: HONEYBADGER_REVISION,
  environment: HONEYBADGER_ENV,
  projectRoot: 'webpack://_N_E/./',
});

Honeybadger.beforeNotify(beforeNotifyHandler);

Honeybadger.afterNotify((err, notice) => {
  if (!isServerSide() && err) {
    axios.post('api/log-event', {
      notice,
      msg: 'Error proxied through server',
    });
  }
});

const MyApp = ({
  Component,
  pageProps,
  err,
  tenant,
  authContext,
  router,
}: AppProps & {
  tenant: TenantData;
  authContext: UserAuthContext;
  err: unknown;
}) => {
  const apolloClient = useApollo(pageProps);

  const [user, setUser] = React.useState<UserQuery['user'] | null>(
    authContext?.user ?? null,
  );

  // Workaround for https://github.com/zeit/next.js/issues/8592 to ensure error is passed along
  const pagePropsWithError = { ...pageProps, err };

  /**
   * Handle tenant and tenantId context.
   *
   * On all page hits from an external source (google, URL bar, etc.) or where user refreshed the page,
   * the request will first come through our custom server and `req` will be decorated with the tenant information.
   *
   * For client-side page changes using the next router, we will _not_ hit the custom server, meaning `req.tenant` and subsequently
   * the `tenant` prop to this component will be undefined; _however_, we can retain that value via a use state.
   *
   * If we're ever in a situation where we have neither a `tenant` nor a `stashedTenant` we should error loudly and return error page.
   */
  const [stashedTenant, setStashedTenant] = React.useState<TenantData | null>(
    null,
  );

  const [stashedTenantId, setStashedTenantId] = React.useState<number | null>(
    null,
  );

  React.useEffect(() => {
    if (tenant) {
      setStashedTenant(tenant);
    }
    if (authContext) {
      setStashedTenantId(authContext.tenantId);
    }
  }, [tenant, authContext]);

  // need to do this rather than just always using `stashedTenant` as the useEffect takes a cycle to update
  const tenantToApply = tenant ?? stashedTenant;
  const tenantId = authContext?.tenantId ?? stashedTenantId;

  React.useEffect(() => {
    if (window.relicxSDK && user && tenantId) {
      window.relicxSDK.identify(user.id, { tenant: tenantId });
    }
  }, [user, tenantId]);
  // Return error page if no tenant available. Error page will handle sending HoneyBadger log.
  // The custom error page will still pass through _app.tsx but not getting tenant context in deployed environment so we're getting
  // misleaading error message about missing tenant data, so don't apply when rerouting to /_error page.
  if ((!tenantToApply || !tenantId) && router.pathname !== '/_error') {
    return (
      <ErrorPage
        statusCode={500}
        hasGetInitialPropsRun={false}
        err={new Error('Missing tenant object or tenantId')}
      />
    );
  }

  return (
    <>
      {/* loads up `window.adsEnabled` */}
      <Script
        type="text/javascript"
        src="/static/js/prebid-ads.js"
        strategy="beforeInteractive"
      />
      {/* fires a script that fetches another script from segment to intialize segment */}
      <Script
        type="text/javascript"
        src="/static/js/analytics.js"
        strategy="beforeInteractive"
      />
      {/* load gapi script. Only certain pages like checkout and login require this, but load up for all b/c otherwise the script won't load on client-side
          routing to such pages
      */}
      <Script
        type="text/javascript"
        src="https://accounts.google.com/gsi/client"
        strategy="beforeInteractive"
      />
      <ApolloProvider client={apolloClient}>
        <ConfigContext.Provider value={tenantToApply.config}>
          <UserContext.Provider value={{ user, setUser, tenantId }}>
            <ThemeProvider theme={createTheme(tenantToApply.theme)}>
              {/* CssBaseline allows theme background default to apply to HTML body */}
              <CssBaseline />
              <AnalyticsWrapper>
                <Head>
                  <meta
                    name="viewport"
                    content="initial-scale=1, width=device-width"
                  />
                  <title>{tenantToApply.config.meta.title}</title>
                  {tenantToApply.config.meta.metaTags.map((tag) => {
                    return (
                      <meta
                        key={`${tag.name}${tag.property}`}
                        name={tag.name}
                        property={tag.property}
                        content={tag.content}
                      />
                    );
                  })}
                  {tenantToApply.config.meta.metaLinks.map((link) => {
                    return (
                      <link
                        key={link.rel + link.href}
                        rel={link.rel}
                        sizes={link.sizes}
                        href={link.href}
                      />
                    );
                  })}
                </Head>
              </AnalyticsWrapper>
              {<Component {...pagePropsWithError} />}
              <GTMAnalytics />
            </ThemeProvider>
          </UserContext.Provider>
        </ConfigContext.Provider>
      </ApolloProvider>
    </>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);
  const tenant = appContext.ctx.req?.tenant;
  const authContext = appContext.ctx.req?.authContext;

  return { ...appProps, tenant, authContext };
};

export default MyApp;
